import type { ModuleSearchResult } from '@/lib/api/search'
import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { SearchableRecordSelect } from '@/components/shared/SearchableRecordSelect'
import { Button } from '@/components/ui/button'
import { DIALOG_FIELD_CLASS, DialogField } from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateTaskMutation } from '@/hooks/mutations/useTaskMutations'
import { useTaskGroupsQuery } from '@/hooks/queries/useTasksQuery'
import { queryKeys } from '@/hooks/query-keys'
import { useProjectPicker } from '@/hooks/useRecordPicker'
import { tasksApi } from '@/lib/api/tasks.api'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { getModuleLinkMeta } from '@/modules/module-link'
import { CircleNotchIcon } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { ModuleTypeEnum } from '@scrambl/shared'

const logger = createLogger('CreateLinkedWorkDialog')

/** Project-scoped work a module picker can create on the spot. */
export type CreatableLinkedWorkType = 'TASK' | 'TASK_GROUP'

const COPY: Record<
  CreatableLinkedWorkType,
  { title: string; noun: string; failed: string }
> = {
  TASK: { title: 'New task', noun: 'Task', failed: 'Could not create task' },
  TASK_GROUP: {
    title: 'New task group',
    noun: 'Group',
    failed: 'Could not create task group',
  },
}

interface CreateLinkedWorkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: CreatableLinkedWorkType
  /** Prefills the name, usually the text the picker searched for. */
  initialName: string
  /** The created record, shaped like a picker result so hosts can link it. */
  onCreated: (result: ModuleSearchResult) => void
}

/**
 * Creates a task or task group inside an existing project from a module
 * picker, so a time entry or field can link to work that doesn't exist yet.
 */
export function CreateLinkedWorkDialog({
  open,
  onOpenChange,
  type,
  initialName,
  onCreated,
}: CreateLinkedWorkDialogProps) {
  const [name, setName] = useState(initialName)
  const [projectId, setProjectId] = useState('')
  const [groupId, setGroupId] = useState('none')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const createTaskMutation = useCreateTaskMutation()
  const copy = COPY[type]
  const Icon = getModuleLinkMeta(type).icon

  const projectPicker = useProjectPicker({
    selectedId: projectId || null,
    enabled: open,
  })
  const { data: groupsData } = useTaskGroupsQuery(
    projectId
      ? { moduleType: ModuleTypeEnum.PROJECT, moduleId: projectId }
      : undefined,
    { enabled: open && type === 'TASK' && !!projectId },
  )
  const groups = groupsData?.data ?? []

  // Each open starts from the picker's search text and a blank project.
  useEffect(() => {
    if (!open) return
    setName(initialName)
    setProjectId('')
    setGroupId('none')
  }, [open, initialName])

  const trimmedName = name.trim()
  const canSubmit = !!trimmedName && !!projectId && !isSubmitting

  const handleProjectChange = (nextProjectId: string) => {
    setProjectId(nextProjectId)
    // Groups belong to one project.
    setGroupId('none')
  }

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault()
    // Hosts are forms too (the time entry dialog), and React events bubble
    // through portals: without this, creating here also saves the host.
    event?.stopPropagation()
    if (!canSubmit) return
    setIsSubmitting(true)
    const projectName = projectPicker.selectedOption?.label
    try {
      if (type === 'TASK') {
        // Module scope (not projectId alone) gives the task its project's
        // numbering.
        const task = await createTaskMutation.mutateAsync({
          taskData: {
            name: trimmedName,
            moduleType: ModuleTypeEnum.PROJECT,
            moduleId: projectId,
            groupId: groupId === 'none' ? undefined : groupId,
          },
        })
        onCreated({
          id: task.id,
          name: task.name,
          type: 'TASK',
          relevanceScore: 1,
          projectId,
          parentName: projectName,
        })
      } else {
        const group = await tasksApi.createTaskGroup({
          name: trimmedName,
          moduleType: ModuleTypeEnum.PROJECT,
          moduleId: projectId,
        })
        // Prefix match: every project's cached group list.
        void queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.all, 'groups'],
        })
        onCreated({
          id: group.id,
          name: group.name,
          type: 'TASK_GROUP',
          relevanceScore: 1,
          projectId,
          parentName: projectName,
        })
      }
      onOpenChange(false)
    } catch (error) {
      logger.error(`${copy.failed}:`, error)
      toast.error(copy.failed)
    } finally {
      setIsSubmitting(false)
    }
  }

  const footer = (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        size="sm"
        className="min-w-[100px]"
        disabled={!canSubmit}
      >
        {isSubmitting ? (
          <>
            <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            Creating…
          </>
        ) : (
          'Create and link'
        )}
      </Button>
    </>
  )

  return (
    <DialogPattern
      open={open}
      onOpenChange={next => {
        if (!isSubmitting) onOpenChange(next)
      }}
      title={copy.title}
      description="It's created in the project you pick, then linked here."
      icon={<Icon className="h-4 w-4 text-primary" />}
      isForm
      formProps={{
        onSubmit: handleSubmit,
        // Keep the host form's shortcuts (Enter / Cmd+Enter to save) out.
        onKeyDown: event => event.stopPropagation(),
      }}
      footer={footer}
    >
      <div className="space-y-4">
        <DialogField label="Name" htmlFor="linked-work-name" required>
          <Input
            id="linked-work-name"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder={`${copy.noun} name`}
            className={DIALOG_FIELD_CLASS}
            autoFocus
          />
        </DialogField>
        <DialogField label="Project" htmlFor="linked-work-project" required>
          <SearchableRecordSelect
            value={projectId || undefined}
            onValueChange={handleProjectChange}
            {...projectPicker.pickerProps}
            placeholder="Select a project"
            searchPlaceholder="Search projects, codes, or clients…"
            emptyMessage="No projects found."
            className={cn(
              DIALOG_FIELD_CLASS,
              'w-full justify-between font-normal',
            )}
          />
        </DialogField>
        {type === 'TASK' && projectId && groups.length > 0 && (
          <DialogField label="Group" htmlFor="linked-work-group">
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger
                id="linked-work-group"
                className={cn(DIALOG_FIELD_CLASS, 'w-full')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No group</SelectItem>
                {groups.map(group => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogField>
        )}
      </div>
    </DialogPattern>
  )
}
